import React, { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import { GET_THEATRICAL_SHOWTIME_ENHANCED_DATA } from '@/services/Theatrical/queries'
import {
  TheatricalShowtimeEnhancedDataQuery,
  TheatricalShowtimeEnhancedDataQueryVariables,
} from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'

interface EnhancedShowtimeContextValues {
  isShowtimeEnhanced: (showtimeId: string) => boolean
}

const EnhancedShowtimeContext = React.createContext<EnhancedShowtimeContextValues | null>(null)

interface EnhancedShowtimeProviderProps {
  venues: ShowtimesVenue[]
}

export const EnhancedShowtimeProvider: ReactFCC<EnhancedShowtimeProviderProps> = ({ children, venues }) => {
  const showtimeIds = useMemo(() => {
    return venues
      ?.map((venue) => venue?.showtimes)
      ?.flat()
      ?.map((showtime) => showtime?.ticketingId ?? '')
      ?.filter((showtimeId) => !!showtimeId)
  }, [venues])

  const { data } = useQuery<TheatricalShowtimeEnhancedDataQuery, TheatricalShowtimeEnhancedDataQueryVariables>(
    GET_THEATRICAL_SHOWTIME_ENHANCED_DATA,
    { variables: { input: { showtimeIds } }, skip: !showtimeIds || showtimeIds?.length === 0, errorPolicy: 'all' },
  )

  const isShowtimeEnhanced = useCallback(
    (showtimeId: string) => {
      const enhancedDataForShowtime = data?.theatricalShowtimeEnhancedData?.enhancedData?.find(
        (enhancedData) => enhancedData?.id === showtimeId,
      )

      if (!enhancedDataForShowtime) {
        return false
      }

      return enhancedDataForShowtime.hasSignificantPurchaseVelocity
    },
    [data?.theatricalShowtimeEnhancedData?.enhancedData],
  )

  const value = useMemo(() => {
    return { isShowtimeEnhanced }
  }, [isShowtimeEnhanced])

  return <EnhancedShowtimeContext.Provider value={value}>{children}</EnhancedShowtimeContext.Provider>
}

export const useEnhancedShowtimeContext = (): EnhancedShowtimeContextValues => {
  return React.useContext(EnhancedShowtimeContext) as EnhancedShowtimeContextValues
}
